import { assign } from 'xstate';
import { routes } from 'config';
import { chooseMovingPlanTemplate } from 'pages/settings/settings.utils';
import { trackEvent } from 'domain/tracking';

const { settings } = routes();

const setContextValue = key => (_, event) => {
  return event.data[key];
};
const goToSettings = (context: MovingStateMachineContext) =>
  context.history.push(settings);
const setSelectedPlan = assign({
  selectedPlan: setContextValue('selectedPlan'),
});
const setSelectedUtilityAccount = assign({
  selectedUtilityAccount: setContextValue('selectedUtilityAccount'),
});
const setNewAccountAttributes = assign({
  zipCode: setContextValue('zipCode'),
  utility: setContextValue('utility'),
  nickname: setContextValue('nickname'),
  enrollmentOptions: setContextValue('enrollmentOptions'),
});
const setMoveRequest = assign({
  moveRequest: setContextValue('moveRequest'),
});
const setNewPlanAttributes = assign({
  newPlanId: setContextValue('newPlanId'),
  newUtilityAccountId: setContextValue('newUtilityAccountId'),
  status: setContextValue('status'),
});
const setCreateOrUpdateMoveRequestError = assign({
  errors: (context, event) => ({
    ...(context as any).errors,
    createOrUpdateMoveRequest: (event as any).data,
  }),
});
const setAccount = assign({
  account: setContextValue('account'),
});
const setNewPlanAmountCents = assign({
  newPlanAmountCents: setContextValue('newPlanAmountCents'),
});
const setNewPlanId = assign({
  newPlanId: setContextValue('newPlanId'),
});
const setRequiresPaymentMethod = assign({
  requiresPaymentMethod: (context: MovingStateMachineContext, event) => {
    const planTemplates = (event as any).data.planTemplateData
      .planTemplates as GetPlanTemplateInfoQuery['planTemplates'];
    const planTemplate = chooseMovingPlanTemplate({
      oldPlanTemplateId: context.selectedPlan?.planTemplateId,
      availablePlanTemplates: planTemplates,
    });
    const UTILITY_BILLED = ['utility_billed', 'ucb'];
    const utilityBilled =
      context.utilityAccountBillingExperience?.every(({ billExperience }) =>
        UTILITY_BILLED.includes(billExperience)
      ) || context.billingExperience[0] === 'ucb';
    if (utilityBilled) {
      return false;
    }
    return planTemplate ? planTemplate.requiresPaymentMethod : true;
  },
});
const setPaymentMethodId = assign({
  paymentMethodId: setContextValue('paymentMethodId'),
});
const setStripeClientSecret = assign({
  stripeClientSecret: setContextValue('stripeClientSecret'),
});
const setExistingPaymentMethodIds = assign({
  existingPaymentMethodIds: setContextValue('existingPaymentMethodIds'),
});
const setUtilityAccountBillingExperience = assign({
  utilityAccountBillingExperience: (
    context: MovingStateMachineContext,
    event
  ) => {
    const utilityAccountBillingExperience = (event as any).data
      ?.utilityAccountBillingExperience;
    return utilityAccountBillingExperience;
  },
});

const setBillingExperience = assign({
  billingExperience: (context: MovingStateMachineContext, event) => {
    // higher == more preferential
    const preference = {
      acb: 4,
      dual: 3,
      ucb: 2,
      none: 1,
    };
    const billingExperienceOptions = (
      event as any
    ).data?.billingExperienceOptions
      .map(option => option.name)
      .sort((a, b) => (preference[b] || 0) - (preference[a] || 0));
    return billingExperienceOptions;
  },
});

export const commonMovingPayload = (context: MovingStateMachineContext) => {
  const utmSource = localStorage.getItem('utmSource');
  const utmMedium = localStorage.getItem('utmMedium');
  const utmCampaign = localStorage.getItem('utmCampaign');
  const payload = {
    accountId: context.accountId,
    userId: context.userId,
    selectedUtilityAccountId: context.selectedUtilityAccount?.id,
    displayServiceAddress:
      context.selectedUtilityAccount?.displayServiceAddress,
    accountNumber: context.selectedUtilityAccount?.accountNumber,
    oldUtility: context.selectedUtilityAccount?.utility?.displayName,
    newUtility: context.utility?.displayName,
    utilityCredentialVerificationStatus:
      context.selectedUtilityAccount?.utilityCredential?.verificationStatus,
    newUtilityAccountId: context.newUtilityAccountId,
    enrollmentOptions: context.enrollmentOptions,
    billingExperience: context.billingExperience,
    newPlanId: context.newPlanId,
    selectedPlanId: context.selectedPlan?.id,
    utmSource,
    utmMedium,
    utmCampaign,
  };
  return payload;
};

const createTrackEvent = (eventName: string) => {
  return (context: MovingStateMachineContext): void => {
    const payload = commonMovingPayload(context);
    trackEvent(eventName, { metadata: payload });
  };
};

const trackSelectedUtilityAccount = createTrackEvent(
  'moving_selected_utility_account'
);
const trackNewAccountAttributesCreated = createTrackEvent(
  'moving_new_account_attributes_created'
);
const trackMoveRequestCreated = createTrackEvent('moving_move_request_created');
const trackPlanAttributesCreated = createTrackEvent(
  'moving_plan_attributes_created'
);
const trackMoveRequestUpdated = createTrackEvent('moving_move_request_updated');
const trackUsingSameUtilityFlow = createTrackEvent(
  'moving_using_same_utility_flow'
);
const trackServiceOnline = createTrackEvent('moving_service_online_flow');
const trackServiceNotOnline = createTrackEvent(
  'moving_service_not_online_flow'
);
const trackAddPaymentMethod = createTrackEvent('moving_add_payment_method');
const trackPaymentMethodCreated = createTrackEvent(
  'moving_payment_method_created'
);

export const actions = {
  goToSettings,
  setSelectedPlan,
  setSelectedUtilityAccount,
  setNewAccountAttributes,
  setMoveRequest,
  setCreateOrUpdateMoveRequestError,
  setAccount,
  setNewPlanAmountCents,
  setNewPlanId,
  setRequiresPaymentMethod,
  setPaymentMethodId,
  setStripeClientSecret,
  setExistingPaymentMethodIds,
  setNewPlanAttributes,
  setUtilityAccountBillingExperience,
  setBillingExperience,
  trackSelectedUtilityAccount,
  trackNewAccountAttributesCreated,
  trackMoveRequestCreated,
  trackAddPaymentMethod,
  trackServiceOnline,
  trackServiceNotOnline,
  trackPaymentMethodCreated,
  trackUsingSameUtilityFlow,
  trackPlanAttributesCreated,
  trackMoveRequestUpdated,
};
